// Chunked, resumable upload (server protocol in backend/app/routes/uploads.py).
import { api, request, ApiError } from "./api.js";

const MAX = 209_715_200;

async function sha256Hex(buf) {
  if (!crypto.subtle) return null; // non-HTTPS origin: server still verifies sizes
  const h = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// The client key is derived from the file + target, so re-selecting the same file after a
// page reload resumes the same server session instead of starting over.
function clientKey(file, target) {
  const id = [file.name, file.size, file.lastModified, target.task_id || target.project_id, target.category].join("|");
  const k = "st_upload:" + id;
  let v = localStorage.getItem(k);
  if (!v) {
    v = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2)) + "-" + file.size;
    localStorage.setItem(k, v);
  }
  return { storageKey: k, value: v };
}

/**
 * uploadFile(file, {task_id|project_id, category, description}, onProgress) -> attachment
 * onProgress({state, sent, total}) where state in: preparing | uploading | verifying | done | error
 */
export async function uploadFile(file, target, onProgress = () => {}, signal) {
  if (file.size > MAX) throw new ApiError(413, "file_too_large", "الحد الأقصى لحجم الملف 200 ميغابايت");
  if (file.size === 0) throw new ApiError(422, "empty", "الملف فارغ");
  const key = clientKey(file, target);
  onProgress({ state: "preparing", sent: 0, total: file.size });
  const session = await api.post("/uploads", {
    client_key: key.value, file_name: file.name, size_bytes: file.size, mime_type: file.type || null, ...target,
  }, { idempotent: false });
  if (session.status === "completed") {
    localStorage.removeItem(key.storageKey);
    return api.post(`/uploads/${session.id}/complete`, {});
  }
  const cs = session.chunk_size;
  const pending = [...session.missing_chunks];
  let sent = (session.total_chunks - pending.length) * cs;
  onProgress({ state: "uploading", sent: Math.min(sent, file.size), total: file.size });

  async function sendChunk(idx) {
    const blob = file.slice(idx * cs, Math.min(file.size, (idx + 1) * cs));
    const buf = await blob.arrayBuffer();
    const sha = await sha256Hex(buf);
    for (let attempt = 0; attempt < 5; attempt++) {
      if (signal?.aborted) throw new DOMException("aborted", "AbortError");
      try {
        await request("PUT", `/uploads/${session.id}/chunks/${idx}`, new Blob([buf]), {
          idempotent: false, signal,
          headers: { "Content-Type": "application/octet-stream", ...(sha ? { "X-Chunk-SHA256": sha } : {}) },
        });
        sent += blob.size;
        onProgress({ state: "uploading", sent: Math.min(sent, file.size), total: file.size });
        return;
      } catch (e) {
        if (e.name === "AbortError" || (e.status && e.status < 500 && e.status !== 0 && e.status !== 408)) throw e;
        await new Promise((r) => setTimeout(r, 800 * 2 ** attempt)); // network hiccup: back off and retry
      }
    }
    throw new ApiError(0, "network", "انقطع الاتصال أثناء الرفع. أعد اختيار الملف لاستكمال الرفع من حيث توقف");
  }

  const workers = Array.from({ length: Math.min(2, pending.length) }, async () => {
    while (pending.length) await sendChunk(pending.shift());
  });
  await Promise.all(workers);
  onProgress({ state: "verifying", sent: file.size, total: file.size });
  const att = await api.post(`/uploads/${session.id}/complete`, {}, { idempotent: false });
  localStorage.removeItem(key.storageKey);
  onProgress({ state: "done", sent: file.size, total: file.size });
  return att;
}
